import Image from 'next/image'
import { Colors } from '@kvika/audur-theme'
import { easeInOut } from '@popmotion/easing'
import { motion, useTransform, useScroll } from 'motion/react'
import { useMediaQuery } from 'react-responsive'
import { useRouter } from 'next/router'

import { AudurButton, AudurButtonLink } from '../../components/audurButtons'
import AudurLink from '../../components/audurLink/AudurLink'
import Select from '../../components/BaseSelect'
import { ChevronDown } from '../../components/BaseSelect/Components'
import { Option } from '../../components/BaseSelect/Constants'
import CirclePlusSvg from '../../components/icons/CirclePlusSvg'
import LockSvg from '../../components/icons/LockSvg'
import { AudurDropdownLink, AudurHeader, AudurLink as AudurLinkType } from '../../prismic/prismicTypes'
import { valueOrDefault } from '../../prismic/prismicUtils'
import { SegmentTrackingId, trackEvent } from '../../utils/Analytics'
import { getNavigationHeaderY } from '../../utils/Constants'
import { Route } from '../../utils/navigation/Routes'
import { LinkStyle } from '../../utils/types/Types'

import { DEFAULT_DROPDOWN_DATA, LOGIN_BUTTON_INDEX } from './dropdownConstants'
import HamburgerMenu from './HamburgerMenu'
import HeaderLinks from './HeaderLinks'
import styles from './Topnavigation.module.scss'

type Props = {
	onMobileMenuToggle(): void
	isMobileMenuOpen?: boolean
	header: AudurHeader
}

const TopNavigation = ({ onMobileMenuToggle, isMobileMenuOpen = false, header }: Props) => {
	const router = useRouter()
	const isMobile = useMediaQuery({ query: '(max-width: 720px)' })
	const logoWidths = isMobile ? [140, 120] : [160, 130]
	const { scrollYProgress } = useScroll()
	const transformedHeaderY = useTransform(scrollYProgress, [0, 0.1], [0, getNavigationHeaderY(isMobile) / 2], {
		ease: easeInOut,
	})

	const transformedLogoWidth = useTransform(scrollYProgress, [0, 0.1], logoWidths, {
		ease: easeInOut,
	})

	const dropdownNavigationData = header.button_group
		?.map((item, index) => {
			const button = item.button as AudurLinkType

			if (index === 0 || (!button.linkTitle && !button.linkUrl)) {
				return {
					...item,
					button: DEFAULT_DROPDOWN_DATA as AudurDropdownLink,
				}
			}

			return item
		})
		.find((item) => !!(item.button as AudurDropdownLink).audur_dropdown_button_title)?.button as
		AudurDropdownLink | undefined

	return (
		<div id='topNavigation' className={styles.outerContainer}>
			<motion.div
				className={styles.navigationContainer}
				style={{ position: 'relative', top: transformedHeaderY }}
				suppressHydrationWarning
			>
				<motion.div className={styles.logoContainer} style={{ width: transformedLogoWidth }} suppressHydrationWarning>
					<AudurLink href={Route.Home} className={styles.logo}>
						<Image
							src={header.image.url}
							alt='Auður logo'
							width={header.image.dimensions.width}
							height={header.image.dimensions.height}
							priority
						/>
					</AudurLink>
				</motion.div>
				<HeaderLinks links={header.link_group ?? []} />
				<div className={styles.navigationButtonContent}>
					<div className={styles.loginButtons}>
						{dropdownNavigationData && (
							<Select
								key={dropdownNavigationData.audur_dropdown_button_title}
								instanceId='navigation'
								inputId='navigation'
								className={styles.navigationDropdownButton}
								labelOnly
								label={
									<AudurButton
										variant='outlined'
										style={{
											color: Colors.White,
											fontWeight: 'bold',
										}}
										aria-haspopup='listbox'
										aria-expanded={false}
									>
										<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
											{dropdownNavigationData.audur_dropdown_button_title} <ChevronDown />
										</div>
									</AudurButton>
								}
								onChange={(option: Option | Option[] | null) => {
									if (option && !Array.isArray(option)) {
										router.push(option.value)
									}
								}}
								options={
									dropdownNavigationData.audur_dropdown_values
										?.map((option) => ({
											label: option.audur_dropdown_value_text ?? '',
											value: option.audur_dropdown_value_link ?? '',
										}))
										.filter((option) => option.label && option.value) || []
								}
							/>
						)}
						{header.button_group?.map((item, index) => {
							const button = item.button as AudurLinkType
							// Hardcoded data until prismic is updated
							if (index === LOGIN_BUTTON_INDEX && button.linkTitle && button.linkUrl) {
								return (
									<AudurButtonLink
										fillColor={button.linkStyle === LinkStyle.Button ? Colors.White : Colors.DarkGray}
										borderColor={button.linkStyle === LinkStyle.DarkButton ? Colors.White : undefined}
										href={valueOrDefault(button.linkUrl, '/')}
										className={button.linkStyle === LinkStyle.Button ? styles.whiteFill : styles.darkFill}

										key={index}
									>
										{valueOrDefault(button.linkTitle, '')}
									</AudurButtonLink>
								)
							}

							return null
						})}
					</div>
					<div className={styles.mobileLoginButton}>
						{dropdownNavigationData && (
							<Select
								key={dropdownNavigationData.audur_dropdown_button_title}
								instanceId='navigation-mobile'
								inputId='navigation'
								className={styles.navigationDropdownButtonMobile}
								labelOnly
								fullSize
								closeMenuOnScroll
								closeMenuOnSelect
								label={
									<AudurButton
										className={styles.mobileAddAccountButton}
										aria-label={`${dropdownNavigationData.audur_dropdown_button_title} navigation dropdown`}
										aria-haspopup='listbox'
										aria-expanded={false}
									>
										<CirclePlusSvg />
									</AudurButton>
								}
								onChange={(option: Option | Option[] | null) => {
									if (option && !Array.isArray(option)) {
										router.push(option.value)
									}
								}}
								options={
									dropdownNavigationData.audur_dropdown_values
										?.map((option) => ({
											label: option.audur_dropdown_value_text ?? '',
											value: option.audur_dropdown_value_link ?? '',
										}))
										.filter((option) => option.label && option.value) || []
								}
							/>
						)}
						<AudurButtonLink
							href={Route.AudurWebBank}
							fillColor={Colors.White}
							style={{ width: 48, height: 48, paddingTop: 4 }}
							onClick={() => {
								trackEvent({ event: SegmentTrackingId.NetbankiButtonClicked })
							}}
							ariaLabel='Netbanki'
						>
							<LockSvg />
						</AudurButtonLink>
					</div>
					<div className={styles.mobileHamburgerButton}>
						<HamburgerMenu
							toggleMenuOpen={onMobileMenuToggle}
							buttonColor={Colors.Orange}
							isMenuOpen={isMobileMenuOpen}
						/>
					</div>
				</div>
			</motion.div>
		</div>
	)
}

export default TopNavigation
